import { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Surveys } from '@/_shared/components/Surveys';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getSurveyCampaignsResultsStatsCall,
  getTemplatesCall,
} from '@/surveys/state';
import { EmptyState } from '../EmptyState';
import { RecapTeamResults } from './RecapTeamResults';
import { SurveyTeamResultsVsDepartmentAndOrganization } from './SurveyTeamResultsVsDepartmentAndOrganization';
import { TeamDistributionBox } from './TeamDistributionBox';

type Props = {
  departmentId?: string;
  teamId?: string;
  teamName?: string;
  activeChip: string;
};

export const AssessmentByTemplate: FC<Props> = ({ departmentId, teamId, teamName, activeChip }) => {
  const t = useTranslations('departments.tabs.department.assessment');
  const tShared = useTranslations();

  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    error: errorGetSurveyCampaignByIdResponsesResultsStats,
    success: successGetSurveyCampaignByIdResponsesResultsStats,
    teamStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { response: responseGetSurveyCampaignsResultsStats } =
    getSurveyCampaignsResultsStatsCall.hook();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const currentTemplateId = useMemo(() => {
    const templateId = responseGetTemplates?.find(template => template.name === activeChip)?.id;
    return templateId;
  }, [activeChip, responseGetTemplates]);

  const currentCampaignId = useMemo(() => {
    const campaign = responseGetSurveyCampaignsResultsStats?.findLast(
      currentCampaign => currentCampaign.surveyTemplateId === currentTemplateId,
    );
    return campaign?.campaignId;
  }, [currentTemplateId, responseGetSurveyCampaignsResultsStats]);

  const nameSurvey = useMemo(() => {
    if (responseGetTemplates && responseGetSurveyCampaignsResultsStats) {
      const templateName = responseGetTemplates.find(
        template => template?.id === currentTemplateId,
      );
      return templateName?.name || '';
    }
    return '';
  }, [responseGetTemplates, responseGetSurveyCampaignsResultsStats, currentTemplateId]);

  const totalCompletedSurveys = useMemo(() => {
    return teamStats?.totalCompletedSurveys || 0;
  }, [teamStats]);

  const totalInvited = useMemo(() => {
    return teamStats?.totalInvited || 0;
  }, [teamStats]);

  const notInvited = teamStats?.totalCompletedSurveys === 0 && teamStats?.totalInvited === 0;

  useEffect(() => {
    if (departmentId && currentCampaignId) {
      executeGetSurveyCampaignByIdResponsesResultsStats({
        campaignId: currentCampaignId,
        departmentId,
        teamId,
      });
    }
  }, [currentCampaignId, departmentId, executeGetSurveyCampaignByIdResponsesResultsStats, teamId]);

  if (loadingGetSurveyCampaignByIdResponsesResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveyCampaignByIdResponsesResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (currentCampaignId === undefined) {
    return <EmptyState />;
  }

  if (
    successGetSurveyCampaignByIdResponsesResultsStats &&
    !loadingGetSurveyCampaignByIdResponsesResultsStats &&
    !errorGetSurveyCampaignByIdResponsesResultsStats
  ) {
    return (
      <Box>
        {(teamStats && teamStats.totalInvited === 0) || notInvited ? (
          <EmptyState />
        ) : (
          <Box mt='m'>
            <Box>
              {totalCompletedSurveys > 0 && (
                <>
                  <Typography variant='h3' mt='xxs' mb='xxs'>
                    {nameSurvey}
                  </Typography>
                  <Typography variant='p3/light' mb='m'>
                    {t(
                      totalCompletedSurveys === 1
                        ? 'intro.description.single'
                        : 'intro.description.plural',
                      {
                        teamName,
                        nameSurvey,
                        totalCompletedSurveys,
                      },
                    )}
                  </Typography>
                </>
              )}
              <Surveys.RecapAssessment
                invitedTotal={totalInvited || 0}
                completedTotal={totalCompletedSurveys || 0}
                linkAssessment={currentTemplateId}
                templateName={nameSurvey}
              />
              {totalCompletedSurveys > 0 && (
                <>
                  <RecapTeamResults currentTemplateId={currentTemplateId} />
                  <Box mt='l'>
                    <SurveyTeamResultsVsDepartmentAndOrganization />
                  </Box>
                  <Box mt='l'>
                    <TeamDistributionBox />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return null;
};
